import * as React from 'react'
import { SupportedRuntimesTooltipResolver } from '../../util/SupportedRuntimesHelper'
import { ResourceSelectorContainer } from '../ResourceSelector/model'
import { ReportsLandingPageSvg } from './ReportsLandingPageSvg'
import { environmentsOptions, ReportsTypes } from './model'
import { Environment } from '../../remote-api/CoreServicesRemoteApi'
import MonitoringLocalStorageManager from '../../util/storage/MonitoringLocalStorageManager'
import Select from '@mulesoft/anypoint-components/lib/Select'
import Button from '@mulesoft/anypoint-components/lib/Button'
import './ReportsLandingPage.css'
import ResourceSelectorLabel
  from '@mulesoft/monitoring-center-components/lib/components/Selector/ResourceSelector/ResourceSelectorLabel/ResourceSelectorLabel'
import BasicSelector from '@mulesoft/monitoring-center-components/lib/components/Selector/BasicSelector/BasicSelector'

export type ValueProps = {
  organizationId: string,
  selectedEnvironment?: string,
  environments: Environment[],
  environmentsLoading: boolean,
  allBusinessGroupEnabled: boolean
}

export type ExternalProps = {}

export type EventProps = {
  fetchApplicationsData: (organizationId: string, envId: string, selectedType: string, dateRange: any) => void,
  fetchEnvironments: (resourceSelectorContainer: ResourceSelectorContainer) => void,
  selectEnvironment: (resourceSelectorContainer: ResourceSelectorContainer) => void,
}

export type Props = ValueProps & ExternalProps

export type AllProps = Props & EventProps

export type State = {
  selectedBusinessGroup?: string,
  environment?: string
}

export class ReportsLandingPage extends React.Component<AllProps, State> {
  
  selectedDateRange: { from: string, to: string }
  
  constructor(props: AllProps) {
    super(props)
    
    this.onClickView = this.onClickView.bind(this)
    this.onChangeOrgSelect = this.onChangeOrgSelect.bind(this)
    this.onChangeEnvSelect = this.onChangeEnvSelect.bind(this)
    this.getResolvedEnvironments = this.getResolvedEnvironments.bind(this)
    this.getResolvedBusinessGroups = this.getResolvedBusinessGroups.bind(this)
    
    const reportsLocalStorage = MonitoringLocalStorageManager.get('reports')
    
    const businessGroup = reportsLocalStorage && reportsLocalStorage.selectedBusinessGroup || props.organizationId
    
    const environment = reportsLocalStorage && reportsLocalStorage.selectedEnvironment || props.selectedEnvironment
    
    if (this.props.allBusinessGroupEnabled) {
      if (businessGroup !== 'all' && businessGroup !== props.organizationId) {
        if (reportsLocalStorage) MonitoringLocalStorageManager.remove('reports')
        
        this.state = {
          selectedBusinessGroup: props.organizationId
        }
      } else {
        this.state = {
          selectedBusinessGroup: businessGroup,
          environment: environment
        }
      }
    } else {
      this.state = {
        selectedBusinessGroup: props.organizationId,
        environment: environmentsOptions.find(opt => opt.value === environment) ? undefined : environment
      }
    }
    
    const from = reportsLocalStorage && reportsLocalStorage.from || 'now-7d'
    const to = reportsLocalStorage && reportsLocalStorage.to || 'now'
    this.selectedDateRange = {from: from, to: to}
  }
  
  componentDidMount() {
    const reportsLocalStorage = MonitoringLocalStorageManager.get('reports')
    const businessGroup = reportsLocalStorage && reportsLocalStorage.selectedBusinessGroup || undefined
    const env = reportsLocalStorage && reportsLocalStorage.selectedEnvironment || undefined
    
    if (businessGroup && env) {
      if ((!this.props.allBusinessGroupEnabled && !environmentsOptions.find(opt => opt.value === env)) || this.props.allBusinessGroupEnabled) {
        const reportsType = reportsLocalStorage.selectedType || 'requests'
        const from = this.selectedDateRange.from
        const to = this.selectedDateRange.to
        
        window.location.hash = `#/reports/organizations/${businessGroup}/environments/${env}/from/${from}/to/${to}/type/${reportsType}`
      }
    }
    if (!this.props.selectedEnvironment) {
      const resourceSelectorContainer = {orgId: this.props.organizationId, isLogFiles: true}
      this.props.fetchEnvironments(resourceSelectorContainer)
    }
  }
  
  onChangeEnvSelect(result: any) {
    const businessGroup = this.state.selectedBusinessGroup
    this.setState({...this.state, environment: result.value})
    if (businessGroup) this.props.fetchApplicationsData(businessGroup, result.value, ReportsTypes.Requests, this.selectedDateRange)
  }
  
  onChangeOrgSelect(result: any) {
    this.setState({...this.state, selectedBusinessGroup: result.value, environment: undefined})
  }
  
  onClickView() {
    const businessGroup = this.state.selectedBusinessGroup
    const env = this.state.environment
    
    if (businessGroup && env) {
      const from = this.selectedDateRange.from
      const to = this.selectedDateRange.to
      const reportsLocalStorage = MonitoringLocalStorageManager.get('reports')
      const reportsType = reportsLocalStorage.selectedType || 'requests'
      
      window.location.hash = `#/reports/organizations/${businessGroup}/environments/${env}/from/${from}/to/${to}/type/${reportsType}`
      
      const newReportsLS = {
        ...reportsLocalStorage,
        selectedBusinessGroup: businessGroup, selectedEnvironment: env, selectedType: reportsType, from: from, to: to
      }
      MonitoringLocalStorageManager.put('reports', newReportsLS)
    }
  }
  
  getResolvedEnvironments(environments: Environment[]) {
    if (!environments) return [{value: '', label: 'Loading...'}]
    else if (this.state.selectedBusinessGroup === 'all') return environmentsOptions
    else {
      return environments.map(env => {
        return {value: env.id, label: env.name}
      })
    }
  }
  
  getResolvedBusinessGroups() {
    return [
      {value: this.props.organizationId, label: 'Current business group'},
      {value: 'all', label: 'All business groups'}
    ]
  }
  
  getValueRenderer(label?: string) {
    if (label) return () => (<ResourceSelectorLabel maxLength={35} className={'reports-landing-page-environment-selector'} label={label}/>)
    else return
  }
  
  getSubtitle(allBusinessGroupEnabled: boolean) {
    if (allBusinessGroupEnabled) {
      return 'Reports give you an understanding of the overall performance and behavior of your applications across business groups and environments.'
    } else {
      return 'Reports give you an understanding of the overall performance and behavior of your applications across any environment. ' +
        'Select an environment to get started.'
    }
  }
  
  render() {
    const {environments, environmentsLoading, allBusinessGroupEnabled} = this.props
    const resolvedBusinessGroups = this.getResolvedBusinessGroups()
    const resolvedEnvironments = this.getResolvedEnvironments(environments)
    const environment = this.state.environment
    const selectedEnvironment = resolvedEnvironments.find(env => env.value === environment)
    const resolvedEnvironmentLabel = selectedEnvironment ? selectedEnvironment.label : environment
    
    return (
      <div className={'reports-landing-page-container'} data-test-id="reports-landing-page-container">
        <div className={'reports-landing-page-header'} data-test-id="reports-landing-page-header">
          <div className={'reports-landing-page-header-title'} data-test-id="reports-landing-page-header-title">
            <span>Reports</span>
          </div>
          <div className={'reports-landing-page-header-subtitle'} data-test-id="reports-landing-page-header-subtitle">
            <span>
              {this.getSubtitle(allBusinessGroupEnabled)}
            </span>
          </div>
        </div>
        <div className={'reports-landing-page-body'} data-test-id="reports-landing-page-body">
          <div className={'reports-landing-page-body-container'} data-test-id="reports-landing-page-body-container">
            <div className={'reports-landing-page-selector-container'} data-test-id="reports-landing-page-selector-container">
              {allBusinessGroupEnabled &&
              <div className={'reports-landing-page-org-select-container'} data-test-id="reports-landing-page-org-select-container">
                <div className={'select-label'} data-test-id="select-label">Business group</div>
                <Select
                  className={'reports-landing-page-org-select'}
                  testId={'org-select'}
                  name="org"
                  options={resolvedBusinessGroups}
                  placeholder={'Select business group'}
                  onChange={this.onChangeOrgSelect}
                  value={this.state.selectedBusinessGroup}
                />
              </div>
              }
              <div className={'reports-landing-page-environment-select-container'} data-test-id="reports-landing-page-environment-select-container">
                <div className={'select-label'} data-test-id="select-label">
                  {this.state.selectedBusinessGroup === 'all' ? 'Environment type' : 'Environment'}
                </div>
                <BasicSelector
                  className={'reports-landing-page-environment-select'}
                  valueRenderer={this.getValueRenderer(resolvedEnvironmentLabel)}
                  testId={'environment-select'}
                  name="environment"
                  options={resolvedEnvironments}
                  isLoading={environmentsLoading}
                  placeholder={this.state.selectedBusinessGroup === 'all' ? 'Select environment type' : 'Select environment'}
                  onChange={this.onChangeEnvSelect}
                  value={environment}
                  maxLength={35}
                />
              </div>
              <Button
                kind={'primary'}
                testId={'view-reports-button'}
                disabled={!selectedEnvironment}
                onClick={this.onClickView}
              >
                View
              </Button>
            </div>
            <SupportedRuntimesTooltipResolver/>
            <ReportsLandingPageSvg/>
          </div>
        </div>
      </div>
    )
  }
  
}
